import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Button,
  FormControlLabel,
  Checkbox,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add, PendingActions, Reply, Visibility, Edit, Archive, Restore, DeleteForever, ExpandMore, ExpandLess, EventNote, Link as LinkIcon } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { formatDateTimeWithTimezone } from '../../../utils/timezoneUtils';
import UpcomingMaintenanceItemSummary, {
  hasUpcomingSource,
  hasAnySource,
  getIntimationSourceDisplay,
  getSourceLabel,
  IntimationStoredSourceSummary,
  useUpcomingItemsMap
} from '../UpcomingMaintenanceItemSummary';

const STATUS_COLORS = {
  PENDING: 'info',
  ACKNOWLEDGED: 'success',
  CLARIFICATION_REQUESTED: 'warning',
  COMPLETED: 'default',
  ARCHIVED: 'default'
};

const COLUMN_COUNT = 8;

/**
 * Tab that lists all "Intimations to Create WO".
 * Planning creates intimations here; CA Manager/CA Technical Officer respond (create WO and acknowledge, or request clarification).
 */
const IntimationsTab = ({
  intimations,
  loadingIntimations,
  showArchivedOnly,
  onShowArchivedChange,
  fetchIntimations,
  canCreateIntimation,
  canLinkIntimationSource,
  canRespondToIntimation,
  canReplyToClarification,
  onOpenCreateDialog,
  onOpenRespondDialog,
  onOpenPlanningReply,
  onLinkToUpcoming,
  setSelectedIntimation,
  onOpenView,
  onOpenEdit,
  showToast,
  displayTimezone,
  aircraft = [],
  upcomingMaintenance = []
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [aircraftFilter, setAircraftFilter] = useState('ALL');
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);

  const filteredIntimations = useMemo(() => {
    const list = Array.isArray(intimations) ? [...intimations] : [];
    const filtered = aircraftFilter === 'ALL'
      ? list
      : list.filter((row) => {
          const id = row.aircraftId?._id || row.aircraftId;
          return id != null && String(id) === String(aircraftFilter);
        });
    // Latest intimations first
    filtered.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
    return filtered;
  }, [intimations, aircraftFilter]);

  const { itemsMap: upcomingItemsMap, loading: loadingUpcomingMap } = useUpcomingItemsMap(
    filteredIntimations,
    upcomingMaintenance,
    !loadingIntimations
  );

  useEffect(() => {
    setPage(0);
    setExpandedRowId(null);
  }, [intimations?.length, aircraftFilter]);

  const paginated = filteredIntimations.length > 0
    ? filteredIntimations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];
  const toggleUpcomingRow = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  const handleArchive = async (row) => {
    if (!window.confirm(`Archive intimation "${row.title}"? You can restore it later.`)) return;
    setActionLoading(row._id);
    try {
      await axios.post(`/api/intimations/${row._id}/archive`);
      fetchIntimations();
      showToast && showToast('Intimation archived.', 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to archive';
      showToast && showToast(msg, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (row) => {
    if (!window.confirm(`Restore intimation "${row.title}" to its previous status?`)) return;
    setActionLoading(row._id);
    try {
      await axios.post(`/api/intimations/${row._id}/restore`);
      fetchIntimations();
      showToast && showToast('Intimation restored.', 'success');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to restore';
      showToast && showToast(msg, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePermanentDelete = async (row) => {
    if (!window.confirm(`Permanently delete intimation "${row.title}"? This cannot be undone.`)) return;
    setActionLoading(row._id);
    try {
      await axios.delete(`/api/intimations/${row._id}`);
      fetchIntimations();
      showToast && showToast('Intimation permanently deleted.', 'success');
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'HAS_DEPENDENCIES' && data?.linkedWorkOrders?.length) {
        const list = data.linkedWorkOrders.map((wo) => `${wo.workOrderNumber || wo._id}: ${wo.title}`).join('\n');
        showToast && showToast(`Cannot delete: linked to work order(s). Remove this intimation from "Linked intimations" on each work order first.`, 'error', 8000);
        alert(`This intimation is linked to the following work order(s). Remove it from "Linked intimations" on each work order (Edit Work Order) before permanently deleting.\n\n${list}`);
      } else if (data?.code === 'NOT_ARCHIVED') {
        showToast && showToast(data.message || 'Archive the intimation first.', 'error');
      } else {
        showToast && showToast(data?.message || 'Failed to delete.', 'error');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const renderUpcomingSourcePanel = (row) => {
    const upcomingItem = upcomingItemsMap[String(row._id)];
    const linkedToUpcoming = hasUpcomingSource(row);

    if (linkedToUpcoming && loadingUpcomingMap && !upcomingItem) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Loading upcoming maintenance row…
        </Typography>
      );
    }

    if (upcomingItem) {
      return (
        <UpcomingMaintenanceItemSummary
          item={upcomingItem}
          displayTimezone={displayTimezone}
          title="Upcoming maintenance source"
        />
      );
    }

    // Always show stored source for linked/ad-hoc intimations (any status), even if live row is gone.
    return (
      <>
        {linkedToUpcoming && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Live upcoming row is not currently available (it may be completed or outside the due window). Showing stored source details:
          </Typography>
        )}
        <IntimationStoredSourceSummary intimation={row} />
      </>
    );
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
            All intimations (requests for CA to create work orders) land here. Planning creates intimations; CA Manager/CA Technical Officer create the work order and acknowledge, or request clarification.
          </Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Aircraft</InputLabel>
            <Select
              value={aircraftFilter}
              label="Aircraft"
              onChange={(e) => setAircraftFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Aircraft</MenuItem>
              {aircraft.map((ac) => (
                <MenuItem key={ac._id} value={ac._id}>
                  {ac.registration || ac._id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!showArchivedOnly}
                onChange={(e) => onShowArchivedChange && onShowArchivedChange(e.target.checked)}
                size="small"
              />
            }
            label="Show Archived only"
          />
        </Box>
        {canCreateIntimation && !showArchivedOnly && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onOpenCreateDialog}
          >
            Create Intimation
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>Created by</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Work order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingIntimations ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  <Typography color="text.secondary">Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredIntimations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  <Typography color="text.secondary">
                    {aircraftFilter !== 'ALL'
                      ? 'No intimations for the selected aircraft.'
                      : 'No intimations yet. Create one to request a work order from CA.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row) => {
                const rowId = String(row._id);
                const linkedToUpcoming = hasUpcomingSource(row);
                const canShowSource = hasAnySource(row);
                const isExpanded = expandedRowId === rowId;
                const sourceDisplay = getIntimationSourceDisplay(row);

                return (
                  <React.Fragment key={row._id}>
                    <TableRow hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          {canShowSource && (
                            <Tooltip title={isExpanded ? 'Hide source' : 'View source'}>
                              <IconButton
                                size="small"
                                onClick={() => toggleUpcomingRow(rowId)}
                                sx={{ mt: -0.25, flexShrink: 0 }}
                                color={isExpanded ? 'primary' : 'default'}
                              >
                                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography variant="body2" fontWeight={500}>{row.title}</Typography>
                              {linkedToUpcoming && (
                                <Chip
                                  icon={<EventNote sx={{ fontSize: '14px !important' }} />}
                                  label="From Upcoming"
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              )}
                            </Box>
                            {row.includedItems && (
                              <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 240 }}>
                                {row.includedItems}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {linkedToUpcoming ? (
                          <Box>
                            <Chip
                              label={getSourceLabel(row.sourceType)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            {(row.sourcePlainText || '').trim() && (
                              <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 180, mt: 0.25 }}>
                                {row.sourcePlainText.trim()}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={sourceDisplay}>
                            {sourceDisplay}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.aircraftId?.registration || '–'}</TableCell>
                      <TableCell>
                        {row.createdBy ? `${row.createdBy.firstName || ''} ${row.createdBy.lastName || ''}`.trim() || '–' : '–'}
                      </TableCell>
                      <TableCell>
                        {row.createdAt ? (displayTimezone ? formatDateTimeWithTimezone(row.createdAt, null, displayTimezone) : dayjs(row.createdAt).format('DD MMM YYYY HH:mm')) : '–'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status || 'PENDING'}
                          color={STATUS_COLORS[row.status] || 'default'}
                          size="small"
                        />
                        {row.status === 'CLARIFICATION_REQUESTED' && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            Clarification requested by {row.clarificationRequestedBy ? `${row.clarificationRequestedBy.firstName || ''} ${row.clarificationRequestedBy.lastName || ''}`.trim() || 'CA' : 'CA'} from Planning.
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(row.workOrderNumber || row.linkedWorkOrderId?.workOrderNumber) ? (
                          <Typography variant="body2">
                            {row.workOrderNumber || row.linkedWorkOrderId?.workOrderNumber}
                          </Typography>
                        ) : (
                          '–'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                          flexWrap="wrap"
                          gap={0.5}
                        >
                          {canLinkIntimationSource && !hasUpcomingSource(row) && row.status !== 'ARCHIVED' && onLinkToUpcoming && (
                            <Tooltip title="Link to Upcoming Maintenance (e.g. AD/SB added after work order started)">
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<LinkIcon />}
                                onClick={() => onLinkToUpcoming(row)}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Link source
                              </Button>
                            </Tooltip>
                          )}
                          {canShowSource && (
                            <Tooltip title={isExpanded ? 'Hide source' : 'View source details'}>
                              <Button
                                size="small"
                                variant={isExpanded ? 'contained' : 'outlined'}
                                startIcon={<EventNote />}
                                onClick={() => toggleUpcomingRow(rowId)}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {isExpanded ? 'Hide source' : 'View source'}
                              </Button>
                            </Tooltip>
                          )}
                          {onOpenView && (
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => onOpenView(row)}
                                disabled={!!actionLoading}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onOpenEdit && row.status !== 'ACKNOWLEDGED' && row.status !== 'COMPLETED' && row.status !== 'ARCHIVED' && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => onOpenEdit(row)}
                                disabled={!!actionLoading}
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canRespondToIntimation && row.status === 'PENDING' && (
                            <Tooltip
                              title="Create a new work order, link an existing WO by number, or request clarification from Planning"
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<PendingActions />}
                                  onClick={() => {
                                    setSelectedIntimation(row);
                                    onOpenRespondDialog();
                                  }}
                                  disabled={!!actionLoading}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  Respond to Intimation
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {canReplyToClarification && row.status === 'CLARIFICATION_REQUESTED' && onOpenPlanningReply && (
                            <Tooltip title="Reply to clarification">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  setSelectedIntimation(row);
                                  onOpenPlanningReply();
                                }}
                                disabled={!!actionLoading}
                              >
                                <Reply />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canCreateIntimation && row.status !== 'ARCHIVED' && (
                            <Tooltip title="Archive (soft delete)">
                              <IconButton
                                size="small"
                                onClick={() => handleArchive(row)}
                                disabled={!!actionLoading}
                              >
                                <Archive />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canCreateIntimation && row.status === 'ARCHIVED' && (
                            <>
                              <Tooltip title="Restore">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRestore(row)}
                                  disabled={!!actionLoading}
                                >
                                  <Restore />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Permanently delete (only when not linked to any work order)">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handlePermanentDelete(row)}
                                  disabled={!!actionLoading}
                                >
                                  <DeleteForever />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {canShowSource && (
                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5 }}>
                              {renderUpcomingSourcePanel(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {filteredIntimations.length > rowsPerPage && (
          <TablePagination
            component="div"
            count={filteredIntimations.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </TableContainer>
    </>
  );
};

export default IntimationsTab;
